<template>
  <div class="base-card" :class="{ 'base-card--loading': loading }">
    <div class="base-card__surface">
      <!-- Loading: shimmer fills the whole card surface. -->
      <div v-if="loading" class="base-card__shimmer" aria-hidden="true"></div>
      <slot v-else>
        <img
          v-if="image"
          class="base-card__image"
          :src="image"
          :alt="imageAlt"
          draggable="false"
        />
        <span v-else class="base-card__placeholder" aria-hidden="true">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect
              x="3"
              y="3"
              width="18"
              height="18"
              rx="2"
              stroke="currentColor"
              stroke-width="1.5"
            />
            <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor" />
            <path
              d="M21 15l-5-5L5 21"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </span>
      </slot>
    </div>
  </div>
</template>

<script setup>
defineOptions({
  name: "BaseCard",
});

defineProps({
  /** Image URL rendered inside the card when no slot content is provided. */
  image: {
    type: String,
    default: "",
  },
  /** Alt text for the image. */
  imageAlt: {
    type: String,
    default: "",
  },
  /** When true, the card renders a shimmer placeholder instead of content. */
  loading: {
    type: Boolean,
    default: false,
  },
});
</script>

<style scoped lang="scss">
// ---------------------------------------------------------------------------
// Rounded card with a circular notch cut out of the bottom edge. The notch is
// a true transparent cut (CSS mask) applied to the inner surface; the outer
// wrapper casts a drop-shadow that follows that cut (a plain box-shadow would
// be clipped away by the mask).
// ---------------------------------------------------------------------------
.base-card {
  --base-card-radius: 14px;
  --base-card-notch-width: 16px;
  --base-card-notch-height: 8px;
  --base-card-notch-x: 24%;
  --base-card-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);

  position: relative;
  width: 100%;
  height: 100%;
  // Portrait ratio so the card keeps its shape when a height is not imposed.
  aspect-ratio: 214 / 400;
  filter: drop-shadow(var(--base-card-shadow));
}

.base-card__surface {
  position: absolute;
  inset: 0;
  border-radius: var(--base-card-radius);
  overflow: hidden;
  background-color: var(--ca-color-backgrounds-surface);

  // Punch a flattened (wide, shallow) elliptical notch in the bottom edge;
  // the drop-shadow follows it.
  --base-card-notch-mask: radial-gradient(
    var(--base-card-notch-width) var(--base-card-notch-height) at
      var(--base-card-notch-x) 100%,
    transparent 0 98%,
    #000 99%
  );
  -webkit-mask-image: var(--base-card-notch-mask);
  mask-image: var(--base-card-notch-mask);
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
}

// Fill the whole card even when the source image is smaller.
// !important defeats the design-system preflight `img { height: auto }`.
.base-card__image {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100% !important;
  object-fit: cover;
}

.base-card__placeholder {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--ca-color-neutrals-on-background-low-40, #9a9a9a);

  svg {
    width: 40px;
    height: 40px;
  }
}

// ---------------------------------------------------------------------------
// Loading shimmer. Self-contained: gradient and animation are defined locally.
// The --ca-shimmer-* custom properties let an ancestor override the colours;
// the fallbacks keep it working standalone.
// ---------------------------------------------------------------------------
.base-card__shimmer {
  position: absolute;
  inset: 0;
  background-image: linear-gradient(
    var(--ca-shimmer-angle, 90deg),
    var(--ca-shimmer-from, var(--ca-color-empresas-solarized-medium, #edecea))
      0%,
    var(--ca-shimmer-to, var(--ca-color-backgrounds-container)) 100%
  );
  background-size: 200% 100%;
  animation: ca-base-card-shimmer 1.5s ease-in-out infinite;
}

@keyframes ca-base-card-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .base-card__shimmer {
    animation: none;
  }
}
</style>
